/**
 * DOCUMENTATION:
 * Component: GCPCaseStudyHRL
 * Purpose: Architectural analysis of the Helicopter Racing League (HRL) case study.
 * * * Note for Junior Developers:
 * - HRL is a global sport. Fans are everywhere, and latency is the enemy of live sports.
 * - Key Problem: Handling 720p/1080p video streams from helicopters to global fans.
 * - Key Problem: Using AI to predict race results in real-time based on telemetry.
 * - Key Problem: Global high availability—if the stream dies, the league loses revenue.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider, List, ListItem, ListItemIcon, ListItemText
} from "@mui/material";

/* Icons */
import SportsIcon from '@mui/icons-material/Sports';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PublicIcon from '@mui/icons-material/Public';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import SpeedIcon from '@mui/icons-material/Speed';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.2)",
    color: "white",
}));

export default function GCPCaseStudyHRL() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    CASE STUDY: HELICOPTER RACING LEAGUE
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>Global Streaming, AI Predictions & High-Speed Telemetry</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>

                {/* 1. The Business Requirement */}
                <Grid item xs={12} md={5}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <SportsIcon sx={{ color: "#EA4335", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>League Overview</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.8 }}>
                            HRL hosts global races with helicopters equipped with hundreds of sensors and high-def cameras.
                            Fans expect a seamless "Real-time" experience on mobile and web.
                        </Typography>
                        <Divider sx={{ my: 2, bgcolor: "rgba(255,255,255,0.1)" }} />
                        <Typography variant="subtitle2" color="#4285F4">Primary Objectives:</Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                            • Move from on-prem to a global cloud-native architecture.<br/>
                            • Reduce video latency to sub-second levels for live fans.<br/>
                            • Use AI to predict "Winner" and "Crash Risk" during the race.<br/>
                            • Expose telemetry data via a public API for 3rd party developers.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* 2. Global Video Delivery Architecture */}
                <Grid item xs={12} md={7}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <LiveTvIcon sx={{ color: "#34A853", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>The Video Pipeline</Typography>
                        </Stack>



                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
                            To serve millions of concurrent viewers globally:
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px", borderTop: "4px solid #4285F4" }}>
                                    <Typography variant="subtitle2" color="#4285F4">Ingest & Transcode</Typography>
                                    <Typography variant="caption">
                                        Use <strong>Transcoder API</strong> to convert raw helicopter streams into multiple
                                        resolutions (4K, 1080p, 720p) for different device bandwidths.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px", borderTop: "4px solid #34A853" }}>
                                    <Typography variant="subtitle2" color="#34A853">Media CDN</Typography>
                                    <Typography variant="caption">
                                        Google's specialized CDN for video. Caches content at the edge (POPs)
                                        to ensure users in South Africa and Alaska get the same low latency.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 3. The AI & Telemetry Engine */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <PsychologyIcon sx={{ color: "#FBBC04", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Real-time Predictions</Typography>
                        </Stack>



                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="#FBBC04">Data Ingestion</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Telemetry from helicopter sensors (speed, altitude, engine temp) is pushed
                                    to <strong>Pub/Sub</strong> at high frequency.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="#FBBC04">Stream Processing</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    <strong>Dataflow</strong> processes the stream, cleaning data and sending it to
                                    <strong> Vertex AI</strong> for real-time inference.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="subtitle2" color="#FBBC04">Prediction Storage</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Predictions are written to <strong>Firestore</strong> (NoSQL) because of its
                                    real-time listeners, which update the fan's mobile app instantly.
                                </Typography>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 4. Security & Scaling Strategy */}
                <Grid item xs={12} md={6}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <PublicIcon sx={{ color: "#4285F4" }} />
                            <Typography variant="h5" fontWeight={800}>Global Availability</Typography>
                        </Stack>



                        <List>
                            <ListItem>
                                <ListItemIcon><SpeedIcon sx={{ color: "#4285F4" }} /></ListItemIcon>
                                <ListItemText
                                    primary="Global HTTP(S) Load Balancing"
                                    secondary="Single IP address for fans worldwide. Routes traffic to the closest GKE cluster."
                                    secondaryTypographyProps={{ style: { color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' } }}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon><VideoLibraryIcon sx={{ color: "#34A853" }} /></ListItemIcon>
                                <ListItemText
                                    primary="Cloud Storage (Multi-Regional)"
                                    secondary="Stores race recordings (VOD). Provides 99.99% availability for historical playback."
                                    secondaryTypographyProps={{ style: { color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' } }}
                                />
                            </ListItem>
                        </List>
                    </SectionCard>
                </Grid>

                {/* 5. Key Architecture Choices */}
                <Grid item xs={12} md={6}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Typography variant="h6" fontWeight={800} color="#EA4335" gutterBottom>Architect's Decision Matrix</Typography>
                        <TableContainer component={Paper} sx={{ bgcolor: "rgba(0,0,0,0.2)", mt: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>Requirement</TableCell>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>GCP Service</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>App Hosting</TableCell>
                                        <TableCell sx={{ color: "#FBBC04" }}>GKE (Global Clusters)</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Race Predictions</TableCell>
                                        <TableCell sx={{ color: "#FBBC04" }}>Vertex AI / AutoML</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Live Stats</TableCell>
                                        <TableCell sx={{ color: "#FBBC04" }}>Firestore (Sync)</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>DDoS Protection</TableCell>
                                        <TableCell sx={{ color: "#FBBC04" }}>Cloud Armor</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionCard>
                </Grid>

                {/* Final Architect Summary */}
                <Grid item xs={12}>
                    <Alert severity="warning" sx={{ bgcolor: "rgba(234, 67, 53, 0.1)", color: "white", border: "1px solid #EA4335" }}>
                        <strong>PCA EXAM TIP:</strong> HRL is the only case study that focuses on <strong>Video/Media</strong>.
                        If a question asks about distributing large video files or live streams globally with low latency,
                        the answer is <strong>Cloud CDN</strong> or <strong>Media CDN</strong>. If it asks about updating mobile apps in real-time,
                        choose <strong>Firestore</strong>.
                    </Alert>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
